import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "../../../Components/UI/dialog";
import { Button } from "../../../Components/UI/button";
import { Input } from "../../../Components/UI/input";
import { Textarea } from "../../../Components/UI/textarea";
import { Label } from "../../../Components/UI/label";
import { Checkbox } from "../../../Components/UI/checkbox";
import { Loader2, X, ArrowLeft, Check, ExternalLink, Upload, Trash2, Eye, ChevronDown, Pencil } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { setGlobalLoading } from "../../../Store/Actions";
import CustomSearchableDropdown from "../../../Components/Common/CustomSearchableDD";
import { segments, states, countries, cityToStateMap } from "../../../Components/Enums/DataEnums";
import { updateCompanyProfile, catalogProductAPI } from "../Profile/ProfileSectionAPI";
import apiService from "../../../Services/APIService";
import { getUploadUrl, isIOSDevice, isMobileDevice } from "../../../Utils/Utilities";
import { getConfigData, STORAGE_KEYS, getActiveAgencyID, getActiveData, getAgencyData } from "../../../Utils/Storage";
import { cropToCenterSquare } from "../../../Utils/ImageCompressor";
import useIsMobile from "../../../Hooks/useIsMobile";
import { SpeechControls } from "../../../Components/Common/SpeechControls";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../../Components/UI/hover-card";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "../../../Components/UI/alert-dialog";
import WebStoreCatalog from "./WebStoreCatalog";


const WebStoreConfig = ({ open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  // Use open prop directly
  const isMobile = useIsMobile();

  // Form data structure - updated with logo & profile fields
  const initialFormData = {
    businessTypes: [],
    whatDoYouSell: "",
    segmentID: "",
    industryID: "",
    businessTagline: "",
    banner: null,
    bannerPreview: null,
    description: "",
    photos: [],
    photosPreviews: [],
    establishedSince: "",
    googleMapsLink: "",
    logo: "",
    logoFile: null,
    logoPreview: null,
    email: "",
    city: "",
    stateID: 0,
    state: "",
    countryID: 1,
    country: "India",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [deleteLogoAlertOpen, setDeleteLogoAlertOpen] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);
  const [isListingOpen, setIsListingOpen] = useState(true);
  const [isBizInfoOpen, setIsBizInfoOpen] = useState(true);
  const [isCatalogOpen, setIsCatalogOpen] = useState(true);
  const [catalogList, setCatalogList] = useState([]);
  const logoFileInputRef = useRef(null);

  // Business type options for checkboxes
  const businessTypeOptions = [
    { id: "M", label: "Manufacturer" },
    { id: "T", label: "Trader" },
    { id: "R", label: "Retailer" },
    { id: "S", label: "Service Provider" },
  ];

  // Convert segments data for dropdown with industry > segment format
  const segmentOptions = segments.map((segment) => ({
    value: segment.SegmentID,
    label: `${segment.Industry} > ${segment.Segment}`,
    segment: segment.Segment,
    industry: segment.Industry,
    industryID: segment.IndustryID,
    searchableText: `${segment.Industry} ${segment.Segment}`.toLowerCase(),
  }));

  // Prepare state options for dropdown (only Indian states)
  const stateOptions = states
    .filter((state) => state.CountryID === 1)
    .map((state) => ({
      value: state.StateID,
      label: state.State,
      code: state.Code,
    }));

  // Prepare country options for dropdown
  const countryOptions = countries.map((country) => ({
    value: country.CountryID,
    label: country.Country,
    code: country.CountryCode,
  }));

  // agency data from Redux store agencyData
  const agencyData = useSelector((state) => state.agencyData.data);

  // Helper to parse catalog JSON from DB or storage
  const parseCatalogData = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter((item) => !item.IsD);
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed.filter((item) => !item.IsD) : [];
    } catch (e) {
      return [];
    }
  };

  // Helper to persist updated catalog to local storage CONFIG_DATA ('C')
  const updateCatalogInStorage = (newCatalog) => {
    try {
      const rawC = localStorage.getItem(STORAGE_KEYS.CONFIG_DATA);
      if (rawC) {
        const parsed = JSON.parse(rawC);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const activeAgencyID = agencyData?.AgencyID ?? agencyData?.agencyID ?? getActiveAgencyID();
          let targetIndex = 0;
          if (activeAgencyID != null) {
            const foundIndex = parsed.findIndex((row) => {
              const rowID = row?.AgencyID ?? row?.NodeID ?? row?.ActiveID;
              return Number(rowID) === Number(activeAgencyID);
            });
            if (foundIndex !== -1) {
              targetIndex = foundIndex;
            }
          }
          parsed[targetIndex].Catalog = typeof newCatalog === "string" ? newCatalog : JSON.stringify(newCatalog);
          localStorage.setItem(STORAGE_KEYS.CONFIG_DATA, JSON.stringify(parsed));
        }
      }
    } catch (e) {
      console.warn("Could not sync Catalog to storage", e);
    }
  };

  // Helper to extract Catalog JSON from setProfile API response
  const extractCatalogFromResponse = (response) => {
    if (!response) return null;
    let pkg = response;
    if (typeof response === "string") {
      try {
        pkg = JSON.parse(response);
      } catch {
        return null;
      }
    }
    if (pkg.updatePackage) {
      try {
        const parsedPkg =
          typeof pkg.updatePackage === "string"
            ? JSON.parse(pkg.updatePackage)
            : pkg.updatePackage;
        pkg = parsedPkg;
      } catch { }
    }

    const activeAgencyID = agencyData?.AgencyID ?? agencyData?.agencyID ?? getActiveAgencyID();

    // 1. Direct property on pkg
    if (pkg?.Catalog !== undefined && pkg?.Catalog !== null) return pkg.Catalog;
    if (pkg?.catalog !== undefined && pkg?.catalog !== null) return pkg.catalog;

    // 2. Search across all tables/arrays in pkg
    const tables = Object.values(pkg || {}).filter((val) => Array.isArray(val));

    for (const tbl of tables) {
      const rowsWithCatalog = tbl.filter(
        (row) => row && (row.Catalog !== undefined || row.catalog !== undefined)
      );

      if (rowsWithCatalog.length > 0) {
        // First priority: find the row matching activeAgencyID
        if (activeAgencyID != null) {
          const matchedRow = rowsWithCatalog.find((row) => {
            const rowID = row?.AgencyID ?? row?.NodeID ?? row?.ActiveID;
            return Number(rowID) === Number(activeAgencyID);
          });
          if (matchedRow) {
            return matchedRow.Catalog ?? matchedRow.catalog;
          }
        }

        // If only 1 row exists with Catalog in this table, use it
        if (rowsWithCatalog.length === 1) {
          return rowsWithCatalog[0].Catalog ?? rowsWithCatalog[0].catalog;
        }

        // If multiple rows exist and none matched activeAgencyID,
        // look for a row with non-empty catalog, else fallback to first
        const nonNullRow = rowsWithCatalog.find(
          (row) =>
            (row.Catalog || row.catalog) &&
            row.Catalog !== "[]" &&
            row.catalog !== "[]"
        );
        if (nonNullRow) {
          return nonNullRow.Catalog ?? nonNullRow.catalog;
        }

        return rowsWithCatalog[0].Catalog ?? rowsWithCatalog[0].catalog;
      }
    }

    return null;
  };

  // Placeholder for agency data from Redux store
  const configData = {
    BusinessTypes: useSelector((state) => state.masterData.bizTypes) || [],
    WhatDoYouSell: useSelector((state) => state.masterData.sellToDesc) || "",
    BusinessTagline: useSelector((state) => state.masterData.tagline) || "",
    Description: useSelector((state) => state.masterData.description) || "",
    GoogleMapsLink: useSelector((state) => state.masterData.locLink) || "",
    SegmentID: useSelector((state) => state.masterData.segmentID) || "",
    Logo: agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo || "",
    Catalog: useSelector((state) => state.masterData.catalog) || getConfigData()?.Catalog || agencyData?.Catalog || "[]",
  };

  const dispatch = useDispatch();

  // Image validation setup
  const imageValidation = {
    banner: {
      maxSize: 2 * 1024 * 1024, // 2MB
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
    },
    photos: {
      maxSize: 1 * 1024 * 1024, // 1MB per photo
      allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
    },
  };

  const isFormInitialized = useRef(false);

  useEffect(() => {
    if (!open) {
      isFormInitialized.current = false;
      return;
    }

    // Only populate form data ONCE when dialog opens so logo upload doesn't reset form fields
    if (open && !isFormInitialized.current) {
      isFormInitialized.current = true;
      const newAgencyData = { ...initialFormData };

      // Load existing data from configData if available
      newAgencyData.businessTypes = typeof configData.BusinessTypes === 'string' ? configData.BusinessTypes.split("").map(type => type.trim()) : [];
      newAgencyData.whatDoYouSell = configData.WhatDoYouSell ? configData.WhatDoYouSell : "";
      newAgencyData.segmentID = configData.SegmentID ? configData.SegmentID : "";
      if (newAgencyData.segmentID) {
        const foundSeg = segmentOptions.find((s) => s.value === newAgencyData.segmentID);
        newAgencyData.industryID = foundSeg ? foundSeg.industryID : "";
      }
      newAgencyData.description = configData.Description ? configData.Description : "";
      newAgencyData.establishedSince = configData.EstablishedSince ? configData.EstablishedSince : "";
      newAgencyData.googleMapsLink = configData.GoogleMapsLink ? configData.GoogleMapsLink : "";

      // Prefill Profile fields (Email, City, State, Country) from agencyData
      newAgencyData.email = agencyData?.Email || agencyData?.email || "";
      newAgencyData.city = agencyData?.City || agencyData?.city || "";

      const existingStateID = agencyData?.StateID || agencyData?.stateID || 0;
      const existingStateName = agencyData?.State || agencyData?.state || agencyData?.StateName || "";
      if (existingStateID) {
        const foundState = states.find((s) => s.StateID === Number(existingStateID));
        newAgencyData.stateID = Number(existingStateID);
        newAgencyData.state = foundState ? foundState.State : existingStateName;
      } else if (existingStateName) {
        const foundState = states.find((s) => s.State.toLowerCase() === existingStateName.toLowerCase());
        newAgencyData.stateID = foundState ? foundState.StateID : 0;
        newAgencyData.state = foundState ? foundState.State : existingStateName;
      } else {
        newAgencyData.stateID = 0;
        newAgencyData.state = "";
      }

      // Auto-match city to state from DataEnums if city exists and state was empty
      if (newAgencyData.city && !newAgencyData.stateID && !newAgencyData.state) {
        const trimmedCity = newAgencyData.city.trim().toLowerCase();
        const foundCityKey = Object.keys(cityToStateMap).find(
          (key) => key.toLowerCase() === trimmedCity
        );
        if (foundCityKey) {
          const stateNameFromMap = cityToStateMap[foundCityKey];
          const matchedStateObj = states.find(
            (s) => s.State.toLowerCase() === stateNameFromMap.toLowerCase()
          );
          if (matchedStateObj) {
            newAgencyData.stateID = matchedStateObj.StateID;
            newAgencyData.state = matchedStateObj.State;
          }
        }
      }

      const existingCountryID = agencyData?.CountryID || agencyData?.countryID || 1;
      const foundCountry = countries.find((c) => c.CountryID === Number(existingCountryID));
      newAgencyData.countryID = Number(existingCountryID);
      newAgencyData.country = foundCountry ? foundCountry.Country : (agencyData?.Country || agencyData?.country || "India");

      const dbTagline = configData.BusinessTagline ? configData.BusinessTagline.trim() : "";
      const defaultTaglineForLoaded = generateTaglineForValues(
        newAgencyData.businessTypes,
        newAgencyData.segmentID,
        newAgencyData.city,
        newAgencyData.state
      );

      if (dbTagline && dbTagline !== defaultTaglineForLoaded) {
        newAgencyData.businessTagline = dbTagline;
        setIsTaglineByDBEdited(true);
        setIsTaglineAutoGenerated(false);
      } else {
        newAgencyData.businessTagline = defaultTaglineForLoaded || dbTagline;
        setIsTaglineByDBEdited(false);
        setIsTaglineAutoGenerated(true);
      }

      // Load existing logo if available
      const existingLogo = agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo || configData.Logo || "";
      newAgencyData.logo = existingLogo;
      newAgencyData.logoPreview = existingLogo
        ? (existingLogo.startsWith("http://") || existingLogo.startsWith("https://")
          ? existingLogo
          : getUploadUrl(`Uploads/${existingLogo}`))
        : null;
      newAgencyData.logoFile = null;

      // Load existing banner if available
      newAgencyData.bannerPreview = configData.BannerPath ? `/Uploads/${configData.BannerPath}` : null;

      // Load existing photos if available
      newAgencyData.photosPreviews = configData.PhotoPaths && configData.PhotoPaths.length > 0
        ? configData.PhotoPaths.map((path) => `/Uploads/${path}`)
        : [];

      setFormData(newAgencyData);
      const rawCatalog = configData.Catalog || agencyData?.Catalog || getConfigData()?.Catalog;
      setCatalogList(parseCatalogData(rawCatalog));

      // Auto-collapse Listing & Business Information if:
      // 1. User has selected at least one business type
      // 2. Has selected industry & segment (segmentID is truthy and not 0)
      // 3. Has uploaded logo (logo/logoPreview is present)
      const hasBusinessType = Array.isArray(newAgencyData.businessTypes) && newAgencyData.businessTypes.length > 0;
      const hasSegment = Boolean(newAgencyData.segmentID && String(newAgencyData.segmentID) !== "0");
      const hasLogo = Boolean(newAgencyData.logo || newAgencyData.logoPreview);

      const shouldAutoCollapse = hasBusinessType && hasSegment && hasLogo;
      if (shouldAutoCollapse) {
        setIsListingOpen(false);
        setIsBizInfoOpen(false);
      } else {
        setIsListingOpen(true);
        setIsBizInfoOpen(true);
      }
    }
  }, [open]);

  const generateTaglineForValues = (businessTypesList, segID, cName, sName) => {
    const city = cName ? cName.trim() : "";
    const state = sName ? sName.trim() : "";
    const selectedSegment = segmentOptions.find(
      (s) => s.value === segID
    );

    const industry = selectedSegment?.industry || "";
    const segment = selectedSegment?.segment || "";

    const businessTypes = businessTypesList || [];
    const businessTypeStr = businessTypes.length > 0 ? businessTypes.length == 1 ? businessTypes.map((id) => {
      const label = businessTypeOptions.find((option) => option.id === id)?.label || id;
      return label;
    }) : "Supplier" : "";

    // BusinessType + Industry + Segment + City + State
    if (businessTypeStr && industry && segment && city && state) {
      return `${businessTypeStr} of ${segment} (${industry}) based in ${city}, ${state}`;
    }

    // BusinessType + Industry + Segment (no city, no state)
    if (businessTypeStr && industry && segment && !city && !state) {
      return `${businessTypeStr} of ${segment} (${industry})`;
    }

    // BusinessType + Industry + Segment + only City
    if (businessTypeStr && industry && segment && city && !state) {
      return `${businessTypeStr} of ${segment} (${industry}) based in ${city}`;
    }

    // BusinessType + Industry + Segment + only State
    if (businessTypeStr && industry && segment && !city && state) {
      return `${businessTypeStr} of ${segment} (${industry}) based in ${state}`;
    }

    // No BusinessType but Industry + Segment + City + State
    if (!businessTypeStr && industry && segment && city && state) {
      return `Supplier of ${segment} (${industry}) based in ${city}, ${state}`;
    }

    // Only Industry + Segment + City (no state)
    if (industry && segment && city && !state) {
      return `Supplier of ${segment} (${industry}) based in ${city}`;
    }

    // Only Industry + Segment + State (no city)
    if (industry && segment && !city && state) {
      return `Supplier of ${segment} (${industry}) based in ${state}`;
    }

    // Only BusinessType + City + State (no industry/segment)
    if (businessTypeStr && !industry && !segment && city && state) {
      return `${businessTypeStr} based in ${city}, ${state}`;
    }

    // Only Industry + Segment (nothing else)
    if (!businessTypeStr && industry && segment && !city && !state) {
      return `Supplier of ${segment} (${industry})`;
    }

    // Only BusinessType + Location
    if (businessTypeStr && (city || state) && !industry && !segment) {
      return `${businessTypeStr} based in ${city || state}`;
    }

    // Fallback → Empty tagline
    return "";
  };

  // function to generate tagline
  const generateTagline = () => {
    return generateTaglineForValues(
      formData.businessTypes,
      formData.segmentID,
      formData.city,
      formData.state
    );
  };

  const [isTaglineAutoGenerated, setIsTaglineAutoGenerated] = useState(true);
  const [isTaglineByDBEdited, setIsTaglineByDBEdited] = useState(false);

  // auto-update tagline whenever city/state/segment changes
  useEffect(() => {
    const suggestion = generateTagline();

    if ((!formData.businessTagline || isTaglineAutoGenerated) && !isTaglineByDBEdited) {
      setFormData((prev) => ({ ...prev, businessTagline: suggestion }));
      setIsTaglineAutoGenerated(true);
    }
  }, [
    formData.businessTypes,
    formData.segmentID,
    formData.city,
    formData.state,
    agencyData?.City,
    agencyData?.State,
  ]);

  const handleClose = () => {
    onClose();
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      handleClose();
    }
  };

  // Unified Catalog Action Handler (ADDPRD, UPDPRD, DLTPRD)
  const handleCatalogAction = async (changeType, payload) => {
    const actionLabel = changeType === "ADDPRD" ? "add product to" : changeType === "UPDPRD" ? "update" : "delete";
    try {
      const response = await catalogProductAPI(changeType, payload, agencyData);
      if (
        response &&
        (response.FnErrCode === -1 ||
          response.success ||
          response.RetCode === 1 ||
          response.code === 1 ||
          response?.updatePackage)
      ) {
        const backendCatalog = extractCatalogFromResponse(response);
        const parsed = backendCatalog ? parseCatalogData(backendCatalog) : [];
        if (parsed.length > 0 || (changeType === "DLTPRD" && backendCatalog)) {
          setCatalogList(parsed);
          updateCatalogInStorage(backendCatalog);
        } else {
          setCatalogList((prev) => {
            let updated;
            switch (changeType) {
              case "ADDPRD":
                updated = [
                  ...prev,
                  {
                    PID: Date.now(),
                    PNm: payload.name,
                    PImg: payload.imageUrl,
                    PTxt: payload.description || "",
                    STID: payload.typeID || 1,
                    STNm: payload.typeName || (payload.typeID === 2 ? "Service" : "Product"),
                    IsD: false,
                  },
                ];
                break;
              case "UPDPRD":
                updated = prev.map((item) =>
                  item.PID === payload.pid
                    ? {
                      ...item,
                      PNm: payload.name,
                      PImg: payload.imageUrl || item.PImg,
                      PTxt: payload.description || "",
                    }
                    : item
                );
                break;
              case "DLTPRD":
                updated = prev.filter((item) => item.PID !== payload);
                break;
              default:
                updated = prev;
            }
            updateCatalogInStorage(updated);
            return updated;
          });
        }
        return { success: true };
      } else {
        const errMsg =
          response?.ErrMsg || response?.message || `Failed to ${actionLabel} catalog.`;
        return { success: false, error: errMsg };
      }
    } catch (err) {
      console.error(`Error during catalog ${changeType}:`, err);
      return {
        success: false,
        error: err.message || `Failed to ${actionLabel} catalog.`,
      };
    }
  };

  // Handle form field changes
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Handle City Change - Auto match state from DataEnums if matched
  const handleCityChange = (val) => {
    const trimmedVal = val.trim().toLowerCase();
    let autoMatchedStateID = formData.stateID;
    let autoMatchedStateName = formData.state;

    if (trimmedVal) {
      const foundCityKey = Object.keys(cityToStateMap).find(
        (key) => key.toLowerCase() === trimmedVal
      );
      if (foundCityKey) {
        const stateNameFromMap = cityToStateMap[foundCityKey];
        const matchedStateObj = states.find(
          (s) => s.State.toLowerCase() === stateNameFromMap.toLowerCase()
        );
        if (matchedStateObj) {
          autoMatchedStateID = matchedStateObj.StateID;
          autoMatchedStateName = matchedStateObj.State;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      city: val,
      stateID: autoMatchedStateID,
      state: autoMatchedStateName,
    }));
    setIsTaglineByDBEdited(false);
    setIsTaglineAutoGenerated(true);
  };

  // Handle State selection
  const handleStateSelect = (value, option) => {
    setFormData((prev) => ({
      ...prev,
      stateID: value || 0,
      state: option ? option.label : "",
    }));
    setIsTaglineByDBEdited(false);
    setIsTaglineAutoGenerated(true);
  };

  // Handle Country selection
  const handleCountrySelect = (value, option) => {
    setFormData((prev) => ({
      ...prev,
      countryID: value || 1,
      country: option ? option.label : "India",
      ...(value !== 1 ? { stateID: 0, state: "" } : {}),
    }));
  };

  // Handle business type checkbox changes
  const handleBusinessTypeChange = (typeID, checked) => {
    setFormData((prev) => ({
      ...prev,
      businessTypes: checked ? [...prev.businessTypes, typeID] : prev.businessTypes.filter((id) => id !== typeID),
    }));
    setIsTaglineByDBEdited(false);
    setIsTaglineAutoGenerated(true);
  };

  // Handle segment selection
  const handleSegmentSelect = (value, option) => {
    setFormData((prev) => ({
      ...prev,
      segmentID: value || "", // SegmentID
      industryID: option?.industryID || "", // IndustryID (store along with segmentID)
    }));
    setIsTaglineByDBEdited(false);
    setIsTaglineAutoGenerated(true);
  };

  // Handle Google Maps link processing
  const handleGoogleMapsLinkChange = (e) => {
    const fullUrl = e.target.value.trim();

    // Try to find "https" in the string
    const httpIndex = fullUrl.indexOf("https://");

    if (httpIndex !== -1) {
      // Extract everything after "https"
      const extractedPart = fullUrl.substring(httpIndex + "https://".length);
      handleFieldChange("googleMapsLink", extractedPart);
    } else {
      // If "https" not found, store as-is
      handleFieldChange("googleMapsLink", fullUrl);
    }
  };

  // Validate image file
  // const validateImage = (file, type = "photos") => {
  //   const config = imageValidation[type];

  //   // Check file type
  //   if (!config.allowedTypes.includes(file.type)) {
  //     return {
  //       isValid: false,
  //       message: `Please select a valid image file. Allowed formats: ${config.allowedTypes.join(
  //         ", "
  //       )}`,
  //     };
  //   }

  //   // Check file size
  //   if (file.size > config.maxSize) {
  //     return {
  //       isValid: false,
  //       message: `File size should be less than ${
  //         config.maxSize / (1024 * 1024)
  //       }MB`,
  //     };
  //   }

  //   return { isValid: true };
  // };

  // Handle banner upload
  // const handleBannerChange = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   // Basic validation
  //   const basicValidation = validateImage(file, "banner");
  //   if (!basicValidation.isValid) {
  //     toast.error(basicValidation.message);
  //     e.target.value = ""; // Clear input
  //     return;
  //   }

  //   // Create preview URL
  //   const previewUrl = URL.createObjectURL(file);

  //   setFormData((prev) => ({
  //     ...prev,
  //     banner: file,
  //     bannerPreview: previewUrl,
  //   }));

  //   toast.success("Banner selected successfully");
  // };

  // Handle photos upload
  // const handlePhotosChange = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   // Basic validation
  //   const basicValidation = validateImage(file, "photos");
  //   if (!basicValidation.isValid) {
  //     toast.error(basicValidation.message);
  //     e.target.value = ""; // Clear input
  //     return;
  //   }

  //   // Create preview
  //   const previewUrl = URL.createObjectURL(file);

  //   setFormData((prev) => ({
  //     ...prev,
  //     photos: [file], // Replace instead of appending
  //     photosPreviews: [previewUrl],
  //   }));

  //   toast.success("Photo selected successfully");
  //   e.target.value = ""; // Reset input
  // };

  // Remove banner
  // const removeBanner = () => {
  //   if (formData.bannerPreview && formData.bannerPreview.startsWith("blob:")) {
  //     URL.revokeObjectURL(formData.bannerPreview);
  //   }

  //   setFormData((prev) => ({
  //     ...prev,
  //     banner: null,
  //     bannerPreview: null,
  //   }));
  // };

  // Remove photo
  // const removePhoto = (index) => {
  //   const photoToRemove = formData.photosPreviews[index];
  //   if (photoToRemove && photoToRemove.startsWith("blob:")) {
  //     URL.revokeObjectURL(photoToRemove);
  //   }

  //   setFormData((prev) => ({
  //     ...prev,
  //     photos: prev.photos.filter((_, i) => i !== index),
  //     photosPreviews: prev.photosPreviews.filter((_, i) => i !== index),
  //   }));
  // };

  // Handle Logo Upload - directly calls AGNLOGO API like DashboardHeader
  const handleLogoChange = async (e) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;

    // Reset input so same file can be chosen again if needed
    e.target.value = "";

    if (!rawFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, WEBP).");
      return;
    }

    if (rawFile.size > 10 * 1024 * 1024) {
      toast.error("Logo image size should be less than 10MB.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      // Automatically crop to maximum possible centered square image
      const file = await cropToCenterSquare(rawFile, 1000, 0.9);

      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo image size should be less than 2MB.");
        return;
      }

      // 1. Upload logo file to 'Profile'
      const uploadResult = await apiService.uploadFile(file, "Profile");
      if (uploadResult?.error) {
        toast.error(uploadResult.message || "Failed to upload logo image.");
        return;
      }

      const relativePath = uploadResult.data?.relativePath || uploadResult.data?.fileName || "";

      // 2. Directly call AGNLOGO API (same API as Dashboard Header)
      const payload = {
        changeType: "AGNLOGO",
        companyName: agencyData?.AgencyName || "",
        stateID: 0,
        taxID: "",
        logo: relativePath,
        banner: ""
      };

      const result = await updateCompanyProfile("AGNLOGO", payload);

      if (result && result.error === false) {
        const fullLogoUrl = getUploadUrl(`Uploads/${relativePath}`);
        setFormData((prev) => ({
          ...prev,
          logo: relativePath,
          logoPreview: fullLogoUrl,
          logoFile: null,
        }));
        toast.success("Logo updated successfully.");
      } else {
        toast.error(result?.message || "Failed to update logo.");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const handleRemoveLogoClick = () => {
    setDeleteLogoAlertOpen(true);
  };

  const confirmRemoveLogo = async () => {
    setRemovingLogo(true);
    try {
      const payload = {
        changeType: "AGNLOGO",
        companyName: agencyData?.AgencyName || "",
        stateID: 0,
        taxID: "",
        logo: "",
        banner: ""
      };
      const result = await updateCompanyProfile("AGNLOGO", payload);
      if (result && result.error === false) {
        setFormData((prev) => ({
          ...prev,
          logo: "",
          logoPreview: null,
          logoFile: null,
        }));
        toast.success("Logo removed.");
      } else {
        toast.error(result?.message || "Failed to remove logo.");
      }
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Failed to remove logo.");
    } finally {
      setRemovingLogo(false);
      setDeleteLogoAlertOpen(false);
    }
  };

  const isMobilePlatform = isMobile || isIOSDevice() || isMobileDevice();

  // Handle form submission
  const handleSubmit = async (shouldPreview = true) => {
    // Synchronously open a blank tab on click for desktop to bypass browser popup blockers
    const previewTab = shouldPreview && !isMobilePlatform ? window.open("about:blank", "_blank") : null;

    dispatch(setGlobalLoading(true));

    try {
      // Trim all string fields before sending
      const payload = {
        ...formData,
        whatDoYouSell: formData.whatDoYouSell?.trim() || "",
        businessTagline: formData.businessTagline?.trim() || "",
        description: formData.description?.trim() || "",
        googleMapsLink: formData.googleMapsLink?.trim() || "",
        businessTypes: formData.businessTypes?.join(""),
        segmentID: formData.segmentID,
        industryID: formData.industryID,
        email: formData.email?.trim() || "",
        city: formData.city?.trim() || "",
        stateID: formData.stateID || 0,
        state: formData.state?.trim() || "",
        countryID: formData.countryID || 1,
        country: formData.country?.trim() || "India",
      };

      const result = await updateCompanyProfile("WEBSTORE", payload);

      if (result.error === false) {
        toast.success("Webstore setup updated.");

        const agnParam = agencyData?.AgencyID || agencyData?.agencyID || agencyData?.Mobile || agencyData?.mobile;

        if (shouldPreview) {
          if (agnParam) {
            if (isMobilePlatform) {
              handleClose();
              navigate(`/store?m=${agnParam}&i=1`);
              if (onSuccess) onSuccess();
              return;
            } else {
              const webstoreUrl = `${window.location.origin}/web/store?m=${agnParam}`;
              if (previewTab && previewTab !== window && !previewTab.closed) {
                previewTab.location.href = webstoreUrl;
              } else {
                window.open(webstoreUrl, "_blank", "noopener,noreferrer");
              }
            }
          } else {
            if (previewTab && previewTab !== window) previewTab.close();
          }
        }

        if (onSuccess) {
          onSuccess();
        } else {
          handleClose();
        }
      } else {
        if (previewTab && previewTab !== window) previewTab.close();
        toast.error(
          result.message || "Failed to update webstore setup."
        );
      }
    } catch (error) {
      if (previewTab && previewTab !== window) previewTab.close();
      toast.error("Failed to update webstore setup. Please try again.");
      console.error("Error saving webstore config:", error);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Cleanup blob URLs when component unmounts
  // useEffect(() => {
  //   return () => {
  //     // Cleanup banner preview
  //     if (
  //       formData.bannerPreview &&
  //       formData.bannerPreview.startsWith("blob:")
  //     ) {
  //       URL.revokeObjectURL(formData.bannerPreview);
  //     }
  // 
  //     // Cleanup photo previews
  //     formData.photosPreviews.forEach((url) => {
  //       if (url && url.startsWith("blob:")) {
  //         URL.revokeObjectURL(url);
  //       }
  //     });
  //   };
  // }, []);

  const handlePreviewWebstore = () => {
    const agnParam = agencyData?.AgencyID || agencyData?.agencyID || agencyData?.Mobile || agencyData?.mobile;
    if (!agnParam) {
      toast.error("Agency details not available for preview.");
      return;
    }
    if (isMobilePlatform) {
      handleClose();
      navigate(`/store?m=${agnParam}&i=1`);
      return;
    }
    const webstoreUrl = `${window.location.origin}/web/store?m=${agnParam}`;
    window.open(webstoreUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="lg:!max-w-[32vw] max-w-[100vw]">
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose
                tabIndex={-1}
                className="p-0 w-auto h-auto flex-shrink-0 flex justify-center"
                onClick={handleClose}
              >
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">Webstore</div>
              </DialogTitle>
            </div>
            {/* Eye icon only preview button for mobile/iOS */}
            {(isIOSDevice() || isMobileDevice()) && (
              <Button
                variant="ghost"
                onClick={handlePreviewWebstore}
                className="p-0 h-8 w-8 text-zinc-700 hover:bg-zinc-150 rounded-lg flex items-center justify-center shrink-0 shadow-none border-none"
              >
                <Eye className="h-6 w-6" />
                <span className="sr-only">Preview</span>
              </Button>
            )}
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Webstore Setup
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose
              className="flex-shrink-0"
              tabIndex={-1}
              onClick={handleClose}
            >
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* Scrollable middle section */}
        <div className="lg:max-h-[70vh] max-h-[90vh] overflow-y-auto px-4 space-y-2">
          {/* Section 1: Listing (Collapsible) */}
          <div className={`border-b border-zinc-200 ${isListingOpen ? "pb-3" : "pb-1"}`}>
            <div
              onClick={() => setIsListingOpen((prev) => !prev)}
              className="flex items-center justify-between cursor-pointer select-none py-2 group"
            >
              <h2 className="text-base font-semibold text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors">
                Listing
              </h2>
              <ChevronDown className={`h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-transform duration-200 shrink-0 ${isListingOpen ? "rotate-180" : ""}`} />
            </div>

            {isListingOpen && (
              <div className="pt-2 space-y-4">
                {/* Business Type Field - Responsive Checkboxes */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Business Type</Label>
                  <div className="flex flex-wrap gap-x-6 gap-y-3">
                    {businessTypeOptions.map((option) => (
                      <div key={option.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={option.id}
                          checked={formData.businessTypes.includes(option.id)}
                          onCheckedChange={(checked) =>
                            handleBusinessTypeChange(option.id, checked)
                          }
                        />
                        <Label
                          htmlFor={option.id}
                          className="text-sm font-normal cursor-pointer whitespace-nowrap"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* What Do You Sell Field (Hidden) */}
                {/* <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="whatDoYouSell" className="text-sm font-medium">
                      What do you sell?
                    </Label>
                    {formData.whatDoYouSell.length >= 185 && (
                      <span className="text-xs text-gray-500">
                        {200 - formData.whatDoYouSell.length} characters left
                      </span>
                    )}
                  </div>
                  <Input
                    id="whatDoYouSell"
                    value={formData.whatDoYouSell}
                    onChange={(e) =>
                      handleFieldChange("whatDoYouSell", e.target.value)
                    }
                    className="w-full sahi-input"
                    placeholder="e.g. Electronics"
                    autoComplete="off"
                    maxLength={200}
                  />
                </div> */}

                {/* Segment Selection Field */}
                <div className="space-y-2">
                  <Label htmlFor="segment" className="text-sm font-medium">
                    Industry & Segment
                  </Label>
                  <CustomSearchableDropdown
                    fieldID="segment"
                    placeholder="Select Industry & Segment"
                    options={segmentOptions}
                    value={formData.segmentID}
                    onSelect={handleSegmentSelect}
                    displayKey="label"
                    valueKey="value"
                    emptyMessage="No industry & segment available"
                    searchEnabled={true}
                    searchPlaceholder="Search by segment or industry..."
                    className="w-full"
                    enableMultiFieldSearch={true}
                    renderOption={(option, isSelected) => (
                      <div className="flex flex-col">
                        <span className={isSelected ? "font-semibold" : ""}>
                          {option.label}
                        </span>
                      </div>
                    )}
                    enableVastSearch={true}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Section 2: Business Information (Collapsible) */}
          <div className={`border-b border-zinc-200 ${isBizInfoOpen ? "pb-3" : "pb-1"}`}>
            <div
              onClick={() => setIsBizInfoOpen((prev) => !prev)}
              className="flex items-center justify-between cursor-pointer select-none py-2 group"
            >
              <h2 className="text-base font-semibold text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors">
                Business Information
              </h2>
              <ChevronDown className={`h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-transform duration-200 shrink-0 ${isBizInfoOpen ? "rotate-180" : ""}`} />
            </div>

            {isBizInfoOpen && (
              <div className="pt-3 space-y-4">
                {/* Business Logo Field */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Business Logo</Label>
                  <input
                    type="file"
                    ref={logoFileInputRef}
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleLogoChange}
                    className="hidden"
                  />

                  {formData.logoPreview ? (
                    <div className="flex items-start gap-2">
                      <div
                        onClick={() => logoFileInputRef.current?.click()}
                        className="relative w-[120px] h-[120px] sm:w-[132px] sm:h-[132px] aspect-square rounded-xl overflow-hidden group bg-zinc-50 cursor-pointer transition-all flex items-center justify-center border border-[var(--border)] hover:border-zinc-400 shrink-0"
                        title="Click to change logo"
                      >
                        <img
                          src={formData.logoPreview}
                          alt="Business Logo Preview"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-200"
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors pointer-events-none" />

                        {/* Floating edit pencil icon overlay (bottom-right) */}
                        <div
                          className="absolute bottom-1.5 right-1.5 h-6 w-6 sm:h-7 sm:w-7 bg-white/95 hover:bg-white text-zinc-700 hover:text-zinc-900 rounded-full shadow-md border border-zinc-200/90 flex items-center justify-center transition-transform group-hover:scale-105 active:scale-95 z-10"
                          title="Change Logo"
                        >
                          <Pencil className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                        </div>
                      </div>

                      {/* Delete action button beside image at the top */}
                      <div className="flex items-center pt-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 sm:h-9 sm:w-9 p-0 sm:text-text-color sm:hover:bg-gray-200 flex items-center justify-center cursor-pointer"
                          tabIndex={-1}
                          onClick={handleRemoveLogoClick}
                          title="Remove Logo"
                        >
                          <Trash2 className="h-4.5 w-4.5" />
                          <span className="sr-only">Remove Logo</span>
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoFileInputRef.current?.click()}
                      className="relative w-[120px] h-[120px] sm:w-[132px] sm:h-[132px] aspect-square rounded-xl bg-zinc-50/60 hover:bg-zinc-100 flex flex-col items-center justify-center gap-1 cursor-pointer transition-all shadow-xs group"
                      title="Upload business logo"
                    >
                      <svg className="absolute inset-0 w-full h-full pointer-events-none rounded-xl" xmlns="http://www.w3.org/2000/svg">
                        <rect
                          x="1.5"
                          y="1.5"
                          width="calc(100% - 3px)"
                          height="calc(100% - 3px)"
                          rx="10"
                          ry="10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeDasharray="5 4"
                          className="text-zinc-300 group-hover:text-zinc-400 transition-colors"
                        />
                      </svg>
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white flex items-center justify-center shadow-xs group-hover:scale-105 transition-transform z-1">
                        <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-zinc-500" />
                      </div>
                      <span className="text-xs font-medium text-[var(--text-color)] z-1">
                        Upload Logo
                      </span>
                      <span className="text-[9px] text-[var(--sub-text-color)] z-1">
                        1:1 square &bull; Max 2MB
                      </span>
                    </button>
                  )}
                </div>

                {/* Business Tagline */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="tagline" className="text-sm font-medium">
                      Business Tagline
                    </Label>
                    {formData.businessTagline.length >= 185 && (
                      <span className="text-xs text-gray-500">
                        {200 - formData.businessTagline.length} characters left
                      </span>
                    )}
                  </div>
                  <Input
                    id="tagline"
                    value={formData.businessTagline}
                    onChange={(e) => {
                      setIsTaglineByDBEdited(false);
                      handleFieldChange("businessTagline", e.target.value);
                      setIsTaglineAutoGenerated(false); // Mark as manually edited
                    }}
                    className="w-full sahi-input"
                    placeholder="e.g. Your one-stop shop for amazing products!"
                    autoComplete="off"
                    maxLength={200}
                  />
                </div>

                {/* Description Field */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="description" className="text-sm font-medium">
                      About
                    </Label>
                    {formData.description.length < 100 && (
                      <span className="text-xs text-gray-500">
                        {100 - formData.description.length} characters required
                      </span>
                    )}
                    {formData.description.length >= 1985 && (
                      <span className="text-xs text-gray-500">
                        {2000 - formData.description.length} characters left
                      </span>
                    )}
                  </div>
                  <div className="relative">
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleFieldChange("description", e.target.value)}
                      className="w-full sahi-input !text-sm min-h-[112px] sm:min-h-[128px] resize-y pr-20"
                      rows={5}
                      placeholder="e.g. Tell us about your webstore"
                      minLength={100}
                      maxLength={2000}
                    />
                    <SpeechControls
                      targetId="description"
                      value={formData.description}
                      onChange={(val) => handleFieldChange("description", val)}
                      isMobile={isMobile}
                    />
                  </div>
                </div>

                {/* City & State Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-medium">
                      City
                    </Label>
                    <Input
                      id="city"
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleCityChange(e.target.value)}
                      className="w-full sahi-input"
                      placeholder="e.g. Gandhinagar"
                      autoComplete="off"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-medium">
                      State
                    </Label>
                    {formData.countryID === 1 ? (
                      <CustomSearchableDropdown
                        fieldID="state"
                        placeholder="Select State"
                        options={stateOptions}
                        value={formData.stateID}
                        onSelect={(val, opt) => handleStateSelect(val, opt)}
                        displayKey="label"
                        valueKey="value"
                        emptyMessage="No state available"
                        searchEnabled={true}
                        searchPlaceholder="Search state..."
                        className="w-full"
                      />
                    ) : (
                      <Input
                        id="state"
                        type="text"
                        value={formData.state}
                        onChange={(e) => {
                          handleFieldChange("state", e.target.value);
                          setIsTaglineByDBEdited(false);
                          setIsTaglineAutoGenerated(true);
                        }}
                        className="w-full sahi-input"
                        placeholder="e.g. State / Region"
                        autoComplete="off"
                      />
                    )}
                  </div>
                </div>

                {/* Country & Business Email Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {/* Country Field */}
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-sm font-medium">
                      Country
                    </Label>
                    <CustomSearchableDropdown
                      fieldID="country"
                      placeholder="Select Country"
                      options={countryOptions}
                      value={formData.countryID}
                      onSelect={(val, opt) => handleCountrySelect(val, opt)}
                      displayKey="label"
                      valueKey="value"
                      emptyMessage="No country available"
                      searchEnabled={true}
                      searchPlaceholder="Search country..."
                      className="w-full"
                    />
                  </div>

                  {/* Business Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium">
                      Business Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleFieldChange("email", e.target.value)}
                      className="w-full sahi-input"
                      placeholder="e.g. contact@yourbusiness.com"
                      autoComplete="off"
                    />
                  </div>
                </div>

                {/* Location (Google Maps Link) Field */}
                <div className="space-y-2 relative">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="location" className="text-sm font-medium">
                      Location
                    </Label>
                    <div className="flex items-center gap-1 relative">
                      <p className="text-muted-foreground text-sm">
                        (Google Maps Link)
                      </p>
                      <HoverCard openDelay={200} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                          >
                            <span className="info-icon">i</span>
                            <span className="sr-only">How to find Google Maps link</span>
                          </Button>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="top"
                          align="start"
                          sideOffset={4}
                          className="w-64 p-3 bg-white border border-gray-200 text-gray-900"
                        >
                          <div className="space-y-2">
                            <h3 className="text-sm font-semibold text-gray-900">
                              How to Find Google Maps Link
                            </h3>
                            <ol className="list-decimal pl-4 text-sm text-gray-600 space-y-1">
                              <li>
                                <p className="text-sm">
                                  Go to{" "}
                                  <a
                                    href="https://www.google.com/maps/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    google.com/maps
                                  </a>
                                  .
                                </p>
                              </li>
                              <li>Search the business name or address.</li>
                              <li>Click "Share" and copy the link.</li>
                            </ol>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        id="location"
                        value={formData.googleMapsLink}
                        onChange={handleGoogleMapsLinkChange}
                        className="flex-1 sahi-input rounded-l-none"
                        placeholder="e.g. https://maps.app.goo.gl/your+business+id"
                        autoComplete="off"
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={!formData.googleMapsLink}
                        onClick={() => {
                          const url = `https://${formData.googleMapsLink}`;
                          window.open(url, "_blank", "noopener,noreferrer");
                        }}
                        className="h-11 sahi-radius"
                      >
                        <ExternalLink />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 3: Catalog (Collapsible) */}
          <div className={`border-b border-zinc-200 ${isCatalogOpen ? "pb-3" : "pb-1"}`}>
            <div
              onClick={() => setIsCatalogOpen((prev) => !prev)}
              className="flex items-center justify-between cursor-pointer select-none py-2 group"
            >
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-[var(--text-color)] group-hover:text-[var(--sahi-green)] transition-colors">
                  Catalog
                </h2>
                <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-medium">
                  {catalogList.length}/10
                </span>
              </div>
              <ChevronDown className={`h-4 w-4 text-zinc-400 group-hover:text-zinc-600 transition-transform duration-200 shrink-0 ${isCatalogOpen ? "rotate-180" : ""}`} />
            </div>

            {isCatalogOpen && (
              <WebStoreCatalog
                catalog={catalogList}
                onAddProduct={(data) => handleCatalogAction("ADDPRD", data)}
                onUpdateProduct={(data) => handleCatalogAction("UPDPRD", data)}
                onDeleteProduct={(pid) => handleCatalogAction("DLTPRD", pid)}
              />
            )}
          </div>
          {/* Banner Upload Field */}
          {/* <div className="space-y-2 mb-4">
            <Label htmlFor="banner" className="text-sm font-medium">
              Banner Image (Max 2MB)
            </Label>
            <div className="space-y-3">
              <Input
                id="banner"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleBannerChange}
                className="w-full sahi-input py-[7px]"
              />
              
              {formData.bannerPreview && (
                <div className="relative">
                  <img
                    src={formData.bannerPreview}
                    alt="Banner Preview"
                    className="w-full h-32 object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={removeBanner}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </div> */}

          {/* Photos Upload Field */}
          {/* <div className="space-y-2 mb-4">
            <Label htmlFor="photos" className="text-sm font-medium">
              Photo (Max 1MB, Only 1 photo allowed)
            </Label>
            <div className="space-y-3">
              <Input
                id="photos"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handlePhotosChange}
                className="w-full sahi-input py-[7px]"
                disabled={formData.photos.length >= 1} // Disable if already uploaded 1
              />

              {formData.photosPreviews.length > 0 && (
                <div className="relative w-32 h-32">
                  <img
                    src={formData.photosPreviews[0]}
                    alt="Uploaded Photo"
                    className="w-full h-full object-cover rounded-lg border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-1 -right-1 h-6 w-6 p-0"
                    onClick={() => removePhoto(0)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div> */}

          {/* Established Since Field */}
          {/* <div className="space-y-2">
            <Label htmlFor="established" className="text-sm font-medium">
              Established Since
            </Label>
            <Input
              id="established"
              type="text"
              placeholder="e.g. 2020"
              value={formData.establishedSince}
              onChange={(e) =>
                handleFieldChange("establishedSince", e.target.value)
              }
              className="w-1/2 sahi-input"
              max={new Date().toISOString().split("T")[0]} // Prevent future dates
            />
          </div> */}
        </div>

        {/* Footer with buttons */}
        <div className="flex items-center justify-start gap-3 lg:px-4 px-4 lg:pb-4 pb-4 flex-shrink-0 pt-3">
          {isIOSDevice() || isMobileDevice() ? (
            <>
              <Button onClick={() => handleSubmit(false)} className="sm:flex-none">
                <Check className="h-4 w-4" />
                Save
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={handlePreviewWebstore}
                className="sahi-secondary-button sm:flex-none"
              >
                <Eye className="h-4 w-4" />
                Preview
              </Button>
            </>
          ) : (
            <Button onClick={() => handleSubmit(true)} className="sm:flex-none">
              <Check className="h-4 w-4" />
              Save & Preview
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} className="sahi-secondary-button sm:flex-none sm:min-w-[90px]">
            <X className="w-3 h-3" />
            Close
          </Button>
        </div>

        {/* SahiCRM Alert Dialog for Logo Removal Confirmation */}
        <AlertDialog open={deleteLogoAlertOpen} onOpenChange={setDeleteLogoAlertOpen}>
          <AlertDialogContent className="max-w-[90vw] sm:max-w-md border border-[var(--border)] rounded-xl bg-white p-4 sm:p-6 shadow-xl text-left space-y-2">
            <AlertDialogHeader className="text-left space-y-2 items-start">
              <AlertDialogTitle className="text-lg sm:text-lg font-bold text-[var(--text-color)] text-left flex items-center gap-2">
                Remove Business Logo
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[13px] sm:text-sm text-[var(--sub-text-color)] text-left leading-relaxed pt-1">
                Are you sure you want to remove your business logo? This will remove the logo from your profile and webstore.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex flex-row !justify-start items-center gap-3">
              <Button
                onClick={confirmRemoveLogo}
                disabled={removingLogo}
                className="flex-1 sm:flex-none sm:min-w-[120px]"
              >
                {removingLogo ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                {removingLogo ? "Removing" : "Yes, Remove"}
              </Button>
              <Button
                variant="outline"
                onClick={() => setDeleteLogoAlertOpen(false)}
                disabled={removingLogo}
                className="sahi-secondary-button flex-1 sm:flex-none sm:min-w-[90px]"
              >
                <X className="w-3.5 h-3.5" /> Cancel
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default WebStoreConfig;
